import { motion } from "framer-motion";
import { Building2 } from "lucide-react";

/**
 * Premium branded splash screen shown right after successful login (~1.6s).
 * Uses branding.app_name + branding.header_logo_url from the config API.
 */
export default function SplashScreen({ branding = { app_name: "ZOA OS", header_logo_url: "" } }) {
  const appName = branding.app_name || "ZOA OS";
  const logoUrl = branding.header_logo_url || "";
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="splash-screen"
      className="fixed inset-0 z-[100] grid place-items-center bg-gradient-to-br from-[#0F172A] via-[#0b1220] to-[#0F172A] overflow-hidden"
    >
      {/* Animated gradient blob */}
      <motion.div
        aria-hidden
        initial={{ scale: 0.6, opacity: 0.15 }}
        animate={{ scale: [0.6, 1, 0.85], opacity: [0.15, 0.4, 0.25] }}
        transition={{ duration: 1.6, ease: "easeInOut" }}
        className="absolute w-[520px] h-[520px] rounded-full bg-emerald-500/25 blur-3xl"
      />
      <motion.div
        aria-hidden
        initial={{ scale: 0.6, opacity: 0.1 }}
        animate={{ scale: [0.6, 1.1, 0.9], opacity: [0.1, 0.3, 0.18] }}
        transition={{ duration: 1.6, ease: "easeInOut", delay: 0.15 }}
        className="absolute w-[320px] h-[320px] rounded-full bg-sky-500/25 blur-3xl -translate-x-32 translate-y-24"
      />

      <div className="relative flex flex-col items-center gap-6 z-10">
        {/* Logo pop-in */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0, rotate: -8 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 18, delay: 0.1 }}
          className="relative"
        >
          {/* Pulsing ring */}
          <motion.div
            aria-hidden
            animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0, 0.6] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 rounded-2xl border-2 border-emerald-400"
          />
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={appName}
              className="w-24 h-24 rounded-2xl object-cover shadow-2xl shadow-emerald-500/30 ring-1 ring-white/10"
            />
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-emerald-600 grid place-items-center shadow-2xl shadow-emerald-500/40 ring-1 ring-white/10">
              <Building2 className="w-11 h-11 text-white" />
            </div>
          )}
        </motion.div>

        {/* App name with staggered letter reveal */}
        <div className="flex flex-col items-center gap-1.5">
          <motion.h1
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.5, ease: "easeOut" }}
            data-testid="splash-app-name"
            className="font-display font-black text-3xl md:text-4xl tracking-wide text-white"
          >
            {appName}
          </motion.h1>
          <motion.p
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.5 }}
            className="text-xs md:text-sm text-white/60 tracking-[0.3em] uppercase"
          >
            Selamat Datang
          </motion.p>
        </div>

        {/* Animated progress bar */}
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 200, opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          className="h-0.5 rounded-full bg-white/10 overflow-hidden"
        >
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
            className="h-full w-1/2 bg-gradient-to-r from-transparent via-emerald-400 to-transparent"
          />
        </motion.div>
      </div>

      {/* Bottom tagline */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.0, duration: 0.4 }}
        className="absolute bottom-6 text-[10px] tracking-[0.4em] text-white/30 uppercase"
      >
        Operating System · Group Management
      </motion.p>
    </motion.div>
  );
}
